//
// Semaglutide (Wegovy®) PGD — AHT weight management
// Source: AHT_Semaglutide_Wegovy_Clinical_Pathway.docx v1.4
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis: 1 pen per dose step = 4 doses = 1 month supply (AHT confirmed).

import type { PGD } from "../../types";

export const pgdAhtWtSemaglutide: PGD = {
  id: "pgd-aht-wt-semaglutide",
  reference: "AHT-WT-SEM",
  version: "1.4",
  drugRef: "semaglutide",
  indication: "Weight management — semaglutide (Wegovy®)",
  validFrom: "",         // TODO: insert valid from date when signed
  expiryDate: "",        // TODO: insert expiry date when signed
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 84 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "remote_permitted",
    // Remote = pharmacist physically present at registered GPhC/CQC premises
    // per GPhC guidance February 2025
  },

  duration: {
    days: 28,
    comment: "28-day supply per dose step (1 pen = 4 doses). Ongoing monthly review.",
  },

  doseBands: [
    {
      id: "sem-step1",
      doseStepId: "sem-0_25mg",
      resolveBy: "single",
      dose: 0.25,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 1–4: starting/initiation dose",
      formulations: [
        {
          formulationId: "semaglutide-0_25mg-pen",
          instruction: "Inject 0.25mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Wegovy 0.25mg pre-filled pen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "sem-step2",
      doseStepId: "sem-0_5mg",
      resolveBy: "single",
      dose: 0.5,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 5–8: +0.25mg if tolerated",
      formulations: [
        {
          formulationId: "semaglutide-0_5mg-pen",
          instruction: "Inject 0.5mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Wegovy 0.5mg pre-filled pen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "sem-step3",
      doseStepId: "sem-1_0mg",
      resolveBy: "single",
      dose: 1.0,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 9–12: +0.5mg if tolerated",
      formulations: [
        {
          formulationId: "semaglutide-1mg-pen",
          instruction: "Inject 1.0mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Wegovy 1.0mg pre-filled pen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "sem-step4",
      doseStepId: "sem-1_7mg",
      resolveBy: "single",
      dose: 1.7,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 13–16: +0.7mg if tolerated",
      formulations: [
        {
          formulationId: "semaglutide-1_7mg-pen",
          instruction: "Inject 1.7mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Wegovy 1.7mg pre-filled pen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "sem-step5",
      doseStepId: "sem-2_4mg",
      resolveBy: "single",
      dose: 2.4,
      frequency: "Once weekly subcutaneous injection",
      comment: "Week 17+: maintenance dose — MAXIMUM",
      formulations: [
        {
          formulationId: "semaglutide-2_4mg-pen",
          instruction: "Inject 2.4mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Wegovy 2.4mg pre-filled pen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
  ],

  preSupplyChecks: [
    // EXCLUSION CRITERIA — Source: Step 2 of AHT Semaglutide pathway v1.4
    {
      id: "ex-bmi-below-27",
      question: "Is the patient's BMI below 27 kg/m² (and not already established on semaglutide)?",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-bmi-27-30-no-comorbidity",
      question: "Is the patient's BMI 27–30 kg/m² without a weight-related comorbidity?",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity",
    },
    {
      id: "ex-bmi-below-22-5-during-treatment",
      question: "Has the patient's BMI fallen below 22.5 kg/m² during treatment?",
      autoAnswerFrom: "bmiBelowFloor225",
    },
    {
      id: "ex-bmi-monitoring-refusal",
      question: "Is the patient refusing to participate in BMI monitoring?",
      autoAnswerFrom: "sharedMonitoringRefusal",
    },
    {
      id: "ex-anaphylaxis-glp1",
      question: "Has the patient had a previous anaphylactic reaction to semaglutide, another GLP-1 receptor agonist, or any incretin mimetic?",
    },
    {
      id: "ex-hypersensitivity-semaglutide",
      question: "Does the patient have known hypersensitivity to semaglutide or any excipient?",
    },
    {
      id: "ex-t2dm-hba1c-high",
      question: "Does the patient have Type 2 diabetes with HbA1c ≥86 mmol/mol?",
      autoAnswerFrom: "t2dmHba1cHigh",
    },
    {
      id: "ex-concurrent-weight-loss-agents",
      question: "Is the patient currently using other weight loss agents, GLP-1 agonists, or incretin mimetics concurrently?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-endocrine-obesity",
      question: "Is the patient's obesity secondary to an endocrine condition under specialist care?",
      autoAnswerFrom: "sharedEndocrineObesity",
    },
    {
      id: "ex-sulphonylureas-insulin",
      question: "Is the patient currently using sulphonylureas or insulin?",
      autoAnswerFrom: "q6SulphonylureaInsulin",
    },
    {
      id: "ex-age-outside-range",
      question: "Is the patient outside the age range of 18–84 years?",
      autoAnswerFrom: "age_under_75",
    },
    {
      id: "ex-virtual-consultation",
      question: "Is this a virtual or online consultation (for initial supply)?",
      hint: "Initial supply must be face-to-face at a registered GPhC/CQC premises.",
      autoAnswerFrom: "face_to_face_confirmed",
    },
  ],

  interactions: [
    {
      id: "int-narrow-therapeutic-index",
      interactionName: "Narrow therapeutic index oral medicines",
      question: "Is the patient taking any narrow therapeutic index oral medicines?",
      hint: "Semaglutide delays gastric emptying — may affect absorption of oral medicines",
      affectedDrugs: ["semaglutide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-oral-contraceptives",
      interactionName: "Oral contraceptives",
      question: "Is the patient using oral contraceptives?",
      hint: "Add barrier method for 4 weeks after starting or any dose escalation. If vomiting/diarrhoea, follow missed pill guidance.",
      affectedDrugs: ["semaglutide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-glp1-incretin-concurrent",
      interactionName: "Concurrent GLP-1/incretin mimetics",
      question: "Is the patient currently taking any GLP-1 agonist or incretin mimetic?",
      hint: "Do not use concurrently with semaglutide.",
      affectedDrugs: ["semaglutide"],
      excludeIfUnmanageable: true,
      managementPlanRequired: false,
    },
  ],

  cautions: [
    {
      id: "caut-gi-dehydration",
      text: "GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk, especially in elderly",
    },
    {
      id: "caut-gastroparesis",
      text: "Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops",
    },
    {
      id: "caut-pancreatitis",
      text: "Pancreatitis suspected — stop immediately; do not restart if confirmed; enzyme elevation alone does not confirm",
    },
    {
      id: "caut-heart-rate",
      text: "Monitor resting heart rate; report palpitations",
    },
    {
      id: "caut-mood-changes",
      text: "Report mood or behavioural changes",
    },
    {
      id: "caut-not-insulin-substitute",
      text: "Not a substitute for insulin",
    },
    {
      id: "caut-aspiration",
      text: "Aspiration risk — advise of delayed gastric emptying before GA or deep sedation",
      adviceText: "Patient should inform anaesthetist/surgical team before any general anaesthetic or deep sedation.",
    },
    {
      id: "caut-developmental-toxicity",
      text: "Animal studies show developmental toxicity — avoid in pregnancy",
    },
  ],

  counselling: [
    "Inject subcutaneously into abdomen, thigh, or upper arm — rotate sites at each injection",
    "Before first use: store 2–8°C; keep cap on to protect from light",
    "After first use: store ≤30°C or refrigerated for up to 6 weeks; keep cap on",
    "Discard if frozen, discoloured, or expired",
    "Record batch number and expiry date",
    "Supervise first dose if needed",
    "Advise patient to inform GP on initiation and at each dose change",
    "Pancreatitis warning: severe abdominal pain, fever, nausea, vomiting, jaundice — attend A&E",
    "Gastroparesis symptoms (bloating, abdominal pain) — discontinue and contact clinic",
    "Encourage adequate fluid intake with GI side effects",
    "Oral contraceptives: add barrier method for 4 weeks after starting or dose escalation",
    "Emergency contraception: copper IUD is most effective option (unaffected by semaglutide)",
    "Documentation retained 8 years",
    "Advise patient's GP on initiation and at each dose change",
  ],

  efficacyReview: {
    timepoint: "6 months at maximum tolerated dose",
    threshold: "≥5% weight loss",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Weight",
      "BMI",
      "Waist circumference (strongly recommended)",
      "Side effects",
      "HbA1c if Type 2 diabetes",
      "Titrate dose every 4+ weeks based on response and tolerance",
    ],
    discontinueIf: [
      "Less than 5% weight loss after 6 months at maximum tolerated dose",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 28,
    drugSpecificReviewDays: 168,
    drugSpecificReviewLabel: "6-month efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss after 6 months at maximum tolerated dose — stop treatment, refer to GP, offer lifestyle support",
    sectionBReviewDays: 168,
    bmiFloorKgM2: 22.5,
    titrationSchedule: undefined,
    maxEpisodesBeforeReview: null,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Anaphylaxis/angioedema: STOP immediately — refer urgently; discontinue permanently",
    "Pancreatitis: STOP immediately — do not restart if confirmed",
    "Gastroparesis: discontinue",
    "Dehydration with GI effects: review; advise fluid intake",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "Black Triangle medicine — ALL suspected ADRs must be reported",
  ],
};
